import { useState, useEffect } from "react";
import api, { formatBRL, formatDate, initials, resolveAvatar } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  BookUser, TrendingUp, TrendingDown, Minus, AlertTriangle, Plus, Trash2,
  Target, Zap, Award, Calendar, Check, Lightbulb, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

const TENDENCIA = {
  melhorou: { label: "Melhorou", ico: TrendingUp, cls: "bg-emerald-100 text-emerald-700" },
  estavel: { label: "Estável", ico: Minus, cls: "bg-slate-100 text-slate-700" },
  piorou: { label: "Piorou", ico: TrendingDown, cls: "bg-red-100 text-red-700" },
};

const STATUS_COMP = {
  pendente: { label: "Pendente", cls: "bg-slate-100 text-slate-700" },
  cumprido: { label: "Cumprido", cls: "bg-emerald-100 text-emerald-700" },
  parcial: { label: "Parcial", cls: "bg-amber-100 text-amber-700" },
  nao_cumprido: { label: "Não Cumprido", cls: "bg-red-100 text-red-700" },
};

export default function GestaoPDI() {
  const [vends, setVends] = useState([]);
  const [nucleos, setNucleos] = useState([]);
  const [filtroNuc, setFiltroNuc] = useState("todos");
  const [selVend, setSelVend] = useState(null); // {id, nome, ...}
  const [historico, setHistorico] = useState([]);
  const [newOpen, setNewOpen] = useState(false);

  const loadList = () => {
    const q = filtroNuc !== "todos" ? `?nucleo_id=${filtroNuc}` : "";
    api.get(`/pdi/vendedoras${q}`).then((r) => setVends(r.data)).catch(() => {});
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [filtroNuc]);
  useEffect(() => { api.get("/nucleos").then((r) => setNucleos(r.data)); }, []);

  const abrirVend = async (v) => {
    setSelVend(v);
    const r = await api.get(`/pdi/vendedora/${v.id}/historico`);
    setHistorico(r.data || []);
  };

  return (
    <div className="space-y-6" data-testid="gestao-pdi">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookUser className="w-6 h-6 text-indigo-600" />
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Gestão PDI</h1>
          </div>
          <p className="text-slate-500 text-sm">Desenvolvimento individual das vendedoras — metodologia ODP+C</p>
        </div>
        <Select value={filtroNuc} onValueChange={setFiltroNuc}>
          <SelectTrigger className="w-44" data-testid="filter-nucleo">
            <SelectValue placeholder="Núcleo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os núcleos</SelectItem>
            {nucleos.map((n) => (<SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vends.map((v) => {
          const ten = v.ultima_tendencia && TENDENCIA[v.ultima_tendencia];
          const alertaCls = v.alerta === "sem_pdi"
            ? "border-red-300 bg-red-50/50"
            : v.alerta === "atrasado"
              ? "border-amber-300 bg-amber-50/50"
              : "border-slate-200";
          return (
            <Card
              key={v.id}
              className={`p-5 rounded-2xl border-2 cursor-pointer hover:shadow-md transition ${alertaCls}`}
              onClick={() => abrirVend(v)}
              data-testid={`pdi-vend-${v.id}`}
            >
              <div className="flex items-start gap-3">
                <Avatar className="w-12 h-12 flex-shrink-0">
                  <AvatarImage src={resolveAvatar(v.avatar_url)} alt={v.nome} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 font-bold">{initials(v.nome)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{v.nome}</div>
                  <div className="text-xs text-slate-500">{v.codigo && `${v.codigo} · `}{v.nucleo_nome}</div>
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="text-slate-600">
                      Última: <b>{v.ultima_data ? formatDate(v.ultima_data) : "nunca"}</b>
                      {v.dias_sem_pdi !== null && v.ultima_data && <span className="text-slate-400"> · há {v.dias_sem_pdi}d</span>}
                    </div>
                    {ten && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${ten.cls}`}>
                        <ten.ico className="w-3 h-3" /> {ten.label}
                      </span>
                    )}
                    <div className="text-slate-600">
                      Compromissos: <b>{v.compromissos_abertos}</b> abertos
                      {v.compromissos_vencidos > 0 && <span className="text-red-600 font-bold"> · {v.compromissos_vencidos} vencidos</span>}
                    </div>
                    {v.alerta && (
                      <div className={`flex items-center gap-1 font-bold ${v.alerta === "sem_pdi" ? "text-red-600" : "text-amber-600"}`}>
                        <AlertTriangle className="w-3 h-3" />
                        {v.alerta === "sem_pdi" ? "Sem PDI registrado" : "PDI atrasado (+30d)"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
        {vends.length === 0 && <div className="text-slate-400 text-sm">Nenhuma vendedora encontrada.</div>}
      </div>

      {/* Histórico + nova reunião */}
      <Dialog open={!!selVend} onOpenChange={(v) => !v && setSelVend(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pdi-vend-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selVend && (
                <>
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={resolveAvatar(selVend.avatar_url)} />
                    <AvatarFallback>{initials(selVend.nome)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-xl">{selVend.nome}</div>
                    <div className="text-xs text-slate-500 font-normal">{selVend.nucleo_nome}</div>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Button onClick={() => setNewOpen(true)} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="btn-nova-reuniao">
              <Plus className="w-4 h-4 mr-1" /> Nova Reunião de PDI
            </Button>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Histórico</div>
              {historico.length === 0 ? (
                <div className="text-slate-400 text-sm py-4 text-center">Nenhuma reunião registrada ainda.</div>
              ) : historico.map((r) => {
                const ten = TENDENCIA[r.tendencia];
                return (
                  <div key={r.id} className="border border-slate-200 rounded-xl p-4 mb-2" data-testid={`pdi-reuniao-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">{formatDate(r.data)}</div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ten.cls}`}>
                        <ten.ico className="w-3 h-3 inline mr-0.5" /> {ten.label}
                      </span>
                    </div>
                    {r.produtividade && (
                      <div className="text-xs text-slate-600 grid grid-cols-4 gap-2 mb-2">
                        <div>Valor: <b>{formatBRL(r.produtividade.valor_vendido || 0)}</b></div>
                        <div>Inscritos: <b>{r.produtividade.inscritos || 0}</b></div>
                        <div>Prosp.: <b>{r.produtividade.prospectados || 0}</b></div>
                        <div>% Meta: <b>{(r.produtividade.percentual_meta || 0).toFixed(0)}%</b></div>
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mt-1">
                      <b>{r.compromissos?.length || 0}</b> compromisso(s) — {(r.compromissos || []).filter(c => c.status === "cumprido").length} cumpridos
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selVend && (
        <NovaReuniaoModal
          open={newOpen}
          onClose={() => setNewOpen(false)}
          vendedora={selVend}
          onSaved={() => { setNewOpen(false); abrirVend(selVend); loadList(); }}
        />
      )}
    </div>
  );
}

function NovaReuniaoModal({ open, onClose, vendedora, onSaved }) {
  const [prep, setPrep] = useState(null);
  const [revisao, setRevisao] = useState({}); // {compromisso_id: {status, obs}}
  const [oportunidades, setOportunidades] = useState([]);
  const [desafios, setDesafios] = useState([]);
  const [tendencia, setTendencia] = useState("estavel");
  const [compromissos, setCompromissos] = useState([]);
  const [obsConf, setObsConf] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !vendedora) return;
    api.get(`/pdi/vendedora/${vendedora.id}/prep`).then((r) => {
      setPrep(r.data);
      setOportunidades(r.data.oportunidades || []);
      setDesafios(r.data.desafios || []);
      setTendencia("estavel");
      setCompromissos([{ id: null, descricao: "", responsavel: "vendedora", prazo: "", status: "pendente" }]);
      setObsConf("");
      // Inicializa status "cumprido" default para revisão
      const rev = {};
      (r.data.compromissos_anteriores || []).forEach((c) => {
        rev[c.id] = { status: "cumprido", obs: "" };
      });
      setRevisao(rev);
    });
  }, [open, vendedora]);

  const addCompromisso = () => {
    setCompromissos((p) => [...p, { id: null, descricao: "", responsavel: "vendedora", prazo: "", status: "pendente" }]);
  };
  const rmCompromisso = (i) => setCompromissos((p) => p.filter((_, idx) => idx !== i));
  const updCompromisso = (i, k, v) => setCompromissos((p) => p.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  const salvar = async () => {
    // Validação
    if (!compromissos.length) { toast.error("Adicione ao menos um compromisso"); return; }
    for (const c of compromissos) {
      if (!c.descricao.trim() || !c.prazo) {
        toast.error("Todo compromisso precisa de descrição e prazo");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        vendedora_id: vendedora.id,
        data: new Date().toISOString(),
        tendencia,
        oportunidades,
        desafios,
        produtividade: prep?.produtividade || {},
        compromissos,
        revisao_anteriores: Object.entries(revisao).map(([id, r]) => ({
          compromisso_id: id, status: r.status, obs: r.obs || null,
        })),
        observacoes_confidenciais: obsConf.trim() || null,
      };
      await api.post("/pdi/reunioes", payload);
      toast.success("Reunião de PDI registrada ✅");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (!prep) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent><div className="text-slate-500 text-sm">Carregando dados...</div></DialogContent>
      </Dialog>
    );
  }

  const anteriores = prep.compromissos_anteriores || [];
  const prod = prep.produtividade || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="nova-reuniao-modal">
        <DialogHeader>
          <DialogTitle>Nova Reunião de PDI — {vendedora?.nome}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* 1. Revisão anteriores */}
          {anteriores.length > 0 && (
            <section className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-4" data-testid="pdi-revisao">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-5 h-5 text-amber-600" />
                <h3 className="font-display font-bold text-slate-900">1. Revisão dos compromissos anteriores</h3>
              </div>
              <div className="space-y-3">
                {anteriores.map((c) => (
                  <div key={c.id} className="border border-amber-200 bg-white rounded-lg p-3">
                    <div className="text-sm font-semibold text-slate-900">{c.descricao}</div>
                    <div className="text-xs text-slate-500 mb-2">Responsável: {c.responsavel} · Prazo: {c.prazo}</div>
                    <div className="grid md:grid-cols-2 gap-2">
                      <Select value={revisao[c.id]?.status || "cumprido"}
                                onValueChange={(v) => setRevisao((p) => ({ ...p, [c.id]: { ...p[c.id], status: v } }))}>
                        <SelectTrigger data-testid={`rev-status-${c.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cumprido">Cumprido</SelectItem>
                          <SelectItem value="parcial">Cumprido Parcialmente</SelectItem>
                          <SelectItem value="nao_cumprido">Não Cumprido</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Observação..." value={revisao[c.id]?.obs || ""}
                              onChange={(e) => setRevisao((p) => ({ ...p, [c.id]: { ...p[c.id], obs: e.target.value } }))}
                              data-testid={`rev-obs-${c.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 2. Oportunidades */}
          <section className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="pdi-oportunidades">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-emerald-600" />
              <h3 className="font-display font-bold text-slate-900">2. Oportunidades <span className="text-xs font-normal text-slate-500">(carregado automaticamente da carteira · editável)</span></h3>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {oportunidades.length === 0 && <div className="text-xs text-slate-400">Nenhuma oportunidade detectada.</div>}
              {oportunidades.map((o, i) => (
                <div key={i} className="text-sm bg-white rounded-lg p-2 border border-slate-100 flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">•</span>
                  <div className="flex-1">
                    <b>{o.municipio_nome}</b>{o.uf && <span className="text-slate-400 text-xs"> / {o.uf}</span>}
                    <span className="text-slate-600"> — {o.detalhe}</span>
                  </div>
                  <button onClick={() => setOportunidades((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* 3. Desafios */}
          <section className="rounded-xl border border-red-200 bg-red-50/40 p-4" data-testid="pdi-desafios">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="font-display font-bold text-slate-900">3. Desafios <span className="text-xs font-normal text-slate-500">(carregado do funil · editável)</span></h3>
            </div>
            <div className="space-y-2">
              {desafios.length === 0 && <div className="text-xs text-slate-400">Nenhum desafio crítico identificado.</div>}
              {desafios.map((d, i) => (
                <div key={i} className="text-sm bg-white rounded-lg p-2 border border-slate-100 flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <div className="flex-1">{d.detalhe}</div>
                  <button onClick={() => setDesafios((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* 4. Produtividade (não-editável) */}
          <section className="rounded-xl border border-blue-200 bg-blue-50/40 p-4" data-testid="pdi-produtividade">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-blue-600" />
              <h3 className="font-display font-bold text-slate-900">4. Produtividade <span className="text-xs font-normal text-slate-500">(snapshot do período)</span></h3>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Prospectados</div><b>{prod.prospectados || 0}</b></div>
              <div><div className="text-xs text-slate-500">Ligações</div><b>{prod.ligacoes || 0}</b></div>
              <div><div className="text-xs text-slate-500">WhatsApps</div><b>{prod.whatsapps || 0}</b></div>
              <div><div className="text-xs text-slate-500">Inscritos</div><b>{prod.inscritos || 0}</b></div>
              <div><div className="text-xs text-slate-500">Valor</div><b>{formatBRL(prod.valor_vendido || 0)}</b></div>
              <div><div className="text-xs text-slate-500">Ticket</div><b>{formatBRL(prod.ticket_medio || 0)}</b></div>
              <div><div className="text-xs text-slate-500">% Meta</div><b className={prod.percentual_meta >= 100 ? "text-emerald-600" : ""}>{(prod.percentual_meta || 0).toFixed(0)}%</b></div>
              <div><div className="text-xs text-slate-500">Maior streak</div><b>{prod.streak_maximo || 0}d</b></div>
            </div>
          </section>

          {/* 5. Tendência */}
          <section data-testid="pdi-tendencia">
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              5. Tendência desta reunião *
            </Label>
            <div className="mt-2 flex gap-2">
              {Object.entries(TENDENCIA).map(([k, t]) => (
                <button
                  key={k}
                  onClick={() => setTendencia(k)}
                  className={`flex-1 rounded-xl px-3 py-3 font-semibold text-sm transition ${tendencia === k ? t.cls + " ring-2 ring-slate-800" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                  data-testid={`tendencia-${k}`}
                >
                  <t.ico className="w-4 h-4 inline mr-1" /> {t.label}
                </button>
              ))}
            </div>
          </section>

          {/* 6. Compromissos */}
          <section className="rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-4" data-testid="pdi-compromissos">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-600" />
                <h3 className="font-display font-bold text-slate-900">6. Compromissos desta reunião *</h3>
              </div>
              <Button size="sm" variant="outline" onClick={addCompromisso} data-testid="btn-add-compromisso">
                <Plus className="w-3 h-3 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {compromissos.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`compromisso-row-${i}`}>
                  <Input className="col-span-6" placeholder="Descrição do compromisso..."
                          value={c.descricao} onChange={(e) => updCompromisso(i, "descricao", e.target.value)}
                          data-testid={`comp-desc-${i}`} />
                  <Select value={c.responsavel} onValueChange={(v) => updCompromisso(i, "responsavel", v)}>
                    <SelectTrigger className="col-span-2" data-testid={`comp-resp-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendedora">Vendedora</SelectItem>
                      <SelectItem value="diretor">Diretor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" className="col-span-3" value={c.prazo}
                          onChange={(e) => updCompromisso(i, "prazo", e.target.value)}
                          data-testid={`comp-prazo-${i}`} />
                  <button onClick={() => rmCompromisso(i)} className="col-span-1 text-slate-400 hover:text-red-600 justify-self-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* 7. Observações confidenciais */}
          <section>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
              7. Observações confidenciais <span className="text-[9px] text-red-600 normal-case font-bold">(APENAS DIRETORIA)</span>
            </Label>
            <Textarea rows={3} value={obsConf} onChange={(e) => setObsConf(e.target.value)}
                        placeholder="Anotações sensíveis, contexto emocional, situações que a vendedora não deve ver..."
                        data-testid="pdi-obs-conf" />
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="pdi-salvar">
            {saving ? "Salvando..." : "Registrar reunião"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
